"use client"
import React, { useState, useEffect, useRef } from 'react';
import { fetchSanityData } from "@/app/actions/sanity";
import Image from 'next/image';
import Link from 'next/link';
import { useCartContext } from "@/context/CartContext";

interface Product {
  _id: string;
  title: string;
  price: number;
  description: string;
  discountPercentage: number;
  imageUrl: string;
  slug: string;
  tags: string[];
  inventory?: number;
}

interface AsgaardproductProps {
  currentProductId: string;
  categoryName?: string;
  tags?: string[];
}

const Asgaardproduct: React.FC<AsgaardproductProps> = ({ currentProductId, categoryName, tags }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [addingItems, setAddingItems] = useState<Set<string>>(new Set());
  const { addToCart } = useCartContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      // Clean tags array and categoryName to prevent injection
      const categoryFilter = categoryName ? `category->name == "${categoryName}"` : 'false';
      const tagsJson = JSON.stringify(tags || []);
      
      let query = `*[_type == "product" && _id != "${currentProductId}" && (${categoryFilter} || count(tags[@ in ${tagsJson}]) > 0)] [0...8] {
          _id,
          title,
          price,
          description,
          discountPercentage,
          "imageUrl": productImage.asset->url,
          "slug": slug.current,
          tags,
          inventory
      }`;

      let data = await fetchSanityData(query);
      if (!data || data.length === 0) {
        // Fallback: fetch any products except current
        const fallbackQuery = `*[_type == "product" && _id != "${currentProductId}"] [0...8] {
            _id,
            title,
            price,
            description,
            discountPercentage,
            "imageUrl": productImage.asset->url,
            "slug": slug.current,
            tags,
            inventory
        }`;
        data = await fetchSanityData(fallbackQuery);
      }
      
      setProducts(data || []);
    } catch (err) {
      console.error("Error fetching related products:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentProductId) {
      fetchProducts();
    }
  }, [currentProductId, categoryName, tags]);

  const handleAddToCart = (product: Product) => {
    if (addingItems.has(product._id)) return;

    setAddingItems(prev => new Set(prev).add(product._id));
    addToCart({ ...product, quantity: 1 });

    setTimeout(() => {
      setAddingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(product._id);
        return newSet;
      });
      setAddedItems(prev => new Set(prev).add(product._id));

      setTimeout(() => {
        setAddedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(product._id);
          return newSet;
        });
      }, 2000);
    }, 400);
  };

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollAmount = clientWidth * 0.75;
      scrollRef.current.scrollTo({
        left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12 gap-2">
        <div className="w-6 h-6 border-2 border-[#B88E2F] border-t-transparent rounded-full animate-spin" />
        <span className="text-gray-500 text-xs font-semibold">Fetching related products...</span>
      </div>
    );
  }

  if (error || products.length === 0) {
    return null; // hide section if no products or error
  }

  return (
    <div className="relative w-full group/slider">
      {/* Small navigation buttons on the side/top */}
      <div className="absolute -top-16 right-4 flex items-center gap-2 z-10">
        <button
          onClick={() => handleScroll('left')}
          className="w-8 h-8 rounded-full border border-gray-200 bg-white hover:bg-black hover:border-black text-gray-700 hover:text-white flex items-center justify-center transition-all duration-300 shadow-sm active:scale-90"
          aria-label="Previous"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => handleScroll('right')}
          className="w-8 h-8 rounded-full border border-gray-200 bg-white hover:bg-black hover:border-black text-gray-700 hover:text-white flex items-center justify-center transition-all duration-300 shadow-sm active:scale-90"
          aria-label="Next"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Carousel Container */}
      <div 
        ref={scrollRef}
        className="w-full overflow-x-auto flex gap-6 pb-6 scrollbar-hide snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product) => {
          const isOutOfStock = typeof product.inventory !== "undefined" && product.inventory <= 0;
          return (
            <div
              key={product._id}
              className="w-[280px] sm:w-[300px] flex-shrink-0 bg-white border border-gray-100 rounded-[24px] shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col p-3.5 snap-start"
            >
              {/* Image container */}
              <Link 
                href={`/${product.slug}`} 
                className="relative overflow-hidden block aspect-square bg-[#F4F4F4] rounded-[20px] p-4 group-hover:opacity-95 transition-opacity"
              >
                <Image
                  src={product.imageUrl}
                  alt={product.title}
                  fill
                  className="object-contain p-2 group-hover:scale-105 transition-transform duration-500"
                  sizes="(max-width: 768px) 100vw, 25vw"
                />

                {isOutOfStock && (
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center rounded-[20px]">
                    <span className="bg-red-600 text-white font-black text-xs tracking-widest uppercase px-3 py-1.5 rounded-lg shadow-md">
                      Out of Stock
                    </span>
                  </div>
                )}
                
                {product.discountPercentage > 0 && !isOutOfStock && (
                  <span className="absolute top-3 left-3 text-[#B34493] font-bold text-[10px] tracking-wider uppercase bg-white/80 backdrop-blur-sm px-2 py-0.5 rounded-md">
                    WAS Rs. {Math.round(product.price / (1 - product.discountPercentage / 100)).toLocaleString()}
                  </span>
                )}
              </Link>

              {/* Info Container */}
              <div className="pt-4 px-1 flex flex-col flex-1">
                {/* Row 1: Title & Discount percentage */}
                <div className="flex justify-between items-start gap-2">
                  <Link href={`/${product.slug}`} className="min-w-0 flex-1">
                    <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate hover:text-[#B88E2F] transition-colors">
                      {product.title}
                    </h3>
                  </Link>
                  {product.discountPercentage > 0 && (
                    <span className="text-[#E23F51] font-bold text-xs sm:text-sm flex-shrink-0">
                      {product.discountPercentage}% OFF
                    </span>
                  )}
                </div>

                {/* Row 2: Description */}
                <p className="text-[11px] sm:text-xs text-gray-400 line-clamp-1 mt-0.5">
                  {product.description || "Premium School & Office Supply"}
                </p>



                {/* Row 4: Pricing & Buy Button */}
                <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-gray-50">
                  {/* Price Box */}
                  <div className="flex flex-col">
                    {product.discountPercentage > 0 && (
                      <span className="text-[10px] sm:text-xs text-gray-400 line-through leading-none mb-0.5">
                        Rs. {Math.round(product.price / (1 - product.discountPercentage / 100)).toLocaleString()}
                      </span>
                    )}
                    <span className="text-sm sm:text-base font-extrabold text-gray-900 leading-none">
                      Rs. {product.price.toLocaleString()}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {/* Add to Cart Icon Button */}
                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={addingItems.has(product._id) || isOutOfStock}
                      className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
                        isOutOfStock
                          ? "bg-gray-100 border-gray-100 text-gray-300 cursor-not-allowed"
                          : addedItems.has(product._id)
                          ? "bg-green-600 border-green-600 text-white shadow-sm"
                          : addingItems.has(product._id)
                          ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                          : "border-gray-200 hover:border-black text-gray-700 hover:text-black bg-white hover:shadow-sm"
                      }`}
                      title={isOutOfStock ? "Out of Stock" : "Add to Cart"}
                    >
                      {addingItems.has(product._id) ? (
                        <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : addedItems.has(product._id) ? (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      )}
                    </button>

                    {/* Buy Now Button */}
                    {isOutOfStock ? (
                      <button
                        disabled
                        className="rounded-full bg-gray-100 text-gray-400 border border-gray-200 font-bold text-xs px-3.5 py-2 cursor-not-allowed whitespace-nowrap"
                      >
                        Sold Out
                      </button>
                    ) : (
                      <Link
                        href={`/${product.slug}`}
                        className="rounded-full bg-black text-white font-bold text-xs px-3.5 py-2 hover:bg-gray-800 transition text-center whitespace-nowrap shadow-sm hover:shadow"
                      >
                        Buy Now
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Asgaardproduct;
